import React, { useEffect, useState } from 'react';
import { Box, Card, Grid, Typography, Button, Collapse, CircularProgress } from '@mui/material';
import axios from 'axios'; // Import axios for API requests
import { Link } from 'react-router-dom'
import { useNavigate } from 'react-router-dom';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CreateIcon from '@mui/icons-material/Create'; // for empty state
import Platform from '../Platform/Platform';
import Header1 from '../Header/Header1';
import Footer2 from '../Footer/Footer2';

const API_URL = process.env.REACT_APP_API_URL;

function MyRequests({ userId, searchResults, onLogout }) {

    useEffect(() => {
        window.scrollTo(0, 0);  // Scroll to top when this page is loaded
    }, []);


    const [courses, setCourses] = useState([]);  // Initialize as an empty array, not null
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedCourse, setExpandedCourse] = useState(null);
    const navigate = useNavigate();
    const [page, setPage] = useState(1);  // Start with page 1
    const [allFetched, setAllFetched] = useState(false);

    const handleRedirect = () => {
        navigate('/meeting-guidelines');  // Redirect to meeting guidelines page
    };

    const fetchMyPosts = async (page) => {
        setLoading(true);

        try {
            const response = await fetch(`${API_URL}/list_my_requests_data/?user_id=${userId}&page=${page}`);
            if (!response.ok) {
                throw new Error('Failed to fetch posts');
            }

            const data = await response.json();

            if (data.length === 0) {
                setAllFetched(true);  // Mark as all data fetched
            } else {
                setCourses((prevCourses) => {
                    const newCourses = data.filter(course => !Array.isArray(prevCourses) || !prevCourses.some(existingCourse => existingCourse.id === course.id));
                    return [...prevCourses, ...newCourses];  // Append new data
                });
            }

            setLoading(false);
        } catch (error) {
            setError(error.message);
            setLoading(false);
        }
    };

    useEffect(() => {
        if (userId && !allFetched) {
            fetchMyPosts(page);
        }
    }, [page, userId]);

    const handleScroll = () => {
        const scrollPosition = window.scrollY;
        const windowHeight = window.innerHeight;
        const documentHeight = document.documentElement.scrollHeight;

        if (windowHeight + scrollPosition >= documentHeight - 1 && !loading && !allFetched) {
            setPage((prevPage) => prevPage + 1);  // Increment page to load the next set of data
        }
    };

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [loading, allFetched]); // Only re-run the effect when `loading` or `allFetched` changes

    useEffect(() => {
        if (searchResults) {
            setCourses(searchResults);  // Reset courses to searchResults if provided
            setPage(1);  // Reset page to 1 on new search
            setAllFetched(false);  // Allow further fetches if new search is performed
        }
    }, [searchResults]);

    if (!userId) {
        return <div>Loading</div>;; // Your own spinner or fallback
    }


    if (loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: '3rem',
                    color: 'text.secondary',
                }}
            >
                <HourglassTopIcon sx={{ fontSize: 40, mb: 2 }} />
                <Typography variant="h6" component="h1" sx={{ letterSpacing: 1 }}>
                    Loading...
                </Typography>
                <CircularProgress sx={{ mt: 2 }} />
            </Box>
        );
    }

    // Error handling
    if (error) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: '3rem',
                    color: 'error.main',
                }}
            >
                <ErrorOutlineIcon sx={{ fontSize: 40, mb: 2 }} />
                <Typography variant="h6" sx={{ letterSpacing: 1, textAlign: 'center' }}>
                    {error}
                </Typography>
            </Box>
        );
    }

    // If no courses found
    if (!courses || courses.length === 0) {
        return (
            <div>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        padding: '3rem',
                        color: 'text.secondary',
                    }}
                >
                    <Box
                        sx={{
                            position: 'fixed', // or 'relative' / 'sticky' based on your layout
                            top: 0,
                            left: 0,
                            width: '100%',
                            zIndex: 1300, // higher than MUI drawers (default is 1200)
                            minHeight: '10vh',
                        }}
                    >
                        <Header1 userId={userId} onLogout={onLogout} />
                    </Box>
                    <Box >
                        <Platform userId={userId} onLogout={onLogout} />
                    </Box>
                    <Box sx={{ minHeight: '60vh', }}>
                        <Typography
                            variant="h3"
                            align="center"
                            fontSize={{ xs: '2rem', sm: '2.5rem', md: '3rem' }}
                            sx={{
                                backgroundImage: 'linear-gradient(45deg, #00bfff, #ff00ff)',
                                mt: 10,
                                letterSpacing: 7,
                                WebkitBackgroundClip: 'text',  // This makes the gradient clip to the text
                                color: 'transparent',  // Makes the text itself transparent to show the gradient
                                // fontWeight: 'bold',  // Optional: you can make the text bold
                            }}
                            gutterBottom
                        >
                            Created Requests
                        </Typography>

                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center', // centers children horizontally
                                textAlign: 'center',  // ensures text is centered
                                mt: 5,
                            }}
                        >
                            <CreateIcon sx={{ fontSize: 50, mb: 2 }} />
                            <Typography variant="h6" sx={{ letterSpacing: 1 }}>
                                Hey, you haven't created any request sessions yet!
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    maxWidth: 400,
                                    mt: 1,
                                    fontSize: '1rem',
                                }}
                            >
                                Go ahead and create one based on your interests and curiosity.
                            </Typography>
                        </Box>

                    </Box>
                </Box>
                <Footer2 />
            </div>
        );
    }

    // Toggle visibility of booked users list
    const handleExpandClick = (courseId) => {
        setExpandedCourse(expandedCourse === courseId ? null : courseId); // Toggle visibility
    };

    const getSessionEndTime = (startTime, durationInMinutes, selectedDate) => {
        const [startHours, startMinutes] = startTime.split(':');
        const sessionStartTime = new Date(selectedDate);
        sessionStartTime.setHours(startHours);
        sessionStartTime.setMinutes(startMinutes);
        sessionStartTime.setSeconds(0);

        // Add the duration to the start time
        const sessionEndTime = new Date(sessionStartTime.getTime() + durationInMinutes * 60 * 1000);  // duration in milliseconds
        return sessionEndTime;
    };

    const handleCourseDetailsNavigation = (courseId, bookedUsers) => {
        // Navigate to the course details page, passing the course ID as a URL parameter
        navigate(`/show-accepted-users/${courseId}`, { state: { bookedUsers } });
    };

    const convertToAMPM = (time) => {
        const [hours, minutes] = time.split(':'); // Get hours and minutes
        const date = new Date();
        date.setHours(parseInt(hours)); // Set hours in 24-hour format
        date.setMinutes(parseInt(minutes)); // Set minutes
        const options = { hour: '2-digit', minute: '2-digit', hour12: true };
        return date.toLocaleTimeString([], options); // Use locale to get 12-hour format with AM/PM
    };

    const convertDuration = (duration) => {
        const hours = Math.floor(duration / 60); // Calculate hours
        const minutes = duration % 60; // Get remaining minutes

        let timeString = "";

        if (hours > 0) {
            timeString += `${hours} hour${hours > 1 ? "s" : ""} `;
        }
        if (minutes > 0) {
            timeString += `${minutes} minute${minutes > 1 ? "s" : ""}`;
        }

        return timeString.trim(); // Remove extra space if no minutes
    };

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        const dd = String(date.getDate()).padStart(2, '0');
        const mm = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
        const yyyy = date.getFullYear();
        return `${dd}-${mm}-${yyyy}`;
    };



    return (
        <div>
            <Box sx={{ padding: "2rem" }}>

                <Box
                    sx={{
                        position: 'fixed', // or 'relative' / 'sticky' based on your layout
                        top: 0,
                        left: 0,
                        width: '100%',
                        zIndex: 1300, // higher than MUI drawers (default is 1200)
                    }}
                >
                    <Header1 userId={userId} onLogout={onLogout} />
                </Box>


                <Typography
                    variant="h3"
                    align="center"
                    fontSize={{ xs: '2rem', sm: '2.5rem', md: '3rem' }}
                    sx={{
                        backgroundImage: 'linear-gradient(45deg, #00bfff, #ff00ff)',
                        mt: 10,
                        letterSpacing: 7,
                        WebkitBackgroundClip: 'text',  // This makes the gradient clip to the text
                        color: 'transparent',  // Makes the text itself transparent to show the gradient
                        // fontWeight: 'bold',  // Optional: you can make the text bold
                    }}
                    gutterBottom
                >
                    Created Requests
                </Typography>



                <Box sx={{ display: 'flex', flexDirection: 'column', }}>
                    <Box>
                        <Platform userId={userId} />
                    </Box>
                    <Box>
                        <Grid container spacing={5} direction="row" justifyContent="center">
                            {courses.map((course) => {
                                const fullDateTimeString = `${course.selectedDate}T${course.startTime}`;
                                const sessionStartTime = new Date(fullDateTimeString);
                                const sessionEndTime = getSessionEndTime(course.startTime, course.duration, course.selectedDate);
                                const currentTime = new Date();
                                const isSessionEnded = currentTime.getTime() >= sessionEndTime.getTime();
                                const isSessionAboutToStart = currentTime.getTime() >= sessionStartTime.getTime() - 10 * 60 * 1000; // 10 minutes before start
                                const isSessionOngoing = currentTime.getTime() >= sessionStartTime.getTime() && currentTime.getTime() <= sessionEndTime.getTime();

                                const formattedTime = convertToAMPM(course.startTime);
                                const formattedDuration = convertDuration(course.duration);

                                return (
                                    <Grid item xs={12} key={course.id}>
                                        <Link
                                            to={`/my-request-tab-details/${course.id}/${userId}`}
                                            style={{ textDecoration: "none" }}
                                        >
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    flexDirection: { xs: "column", sm: "row" },
                                                    alignItems: "center",
                                                    justifyContent: "space-between",
                                                    gap: 2,
                                                    p: 2,
                                                    backgroundColor: "#000000",
                                                    color: "#e0e0e0",
                                                    borderBottom: "1px solid #e0e0e0",
                                                    borderRadius: 0,
                                                    transition: "background-color 0.2s ease-in-out",
                                                    cursor: "pointer",
                                                    '&:hover': {
                                                        backgroundColor: "#2222",
                                                    },
                                                    textAlign: "center",
                                                    width: "100%",
                                                }}
                                            >
                                                {/* Course Title */}
                                                <Box sx={{ flex: 3, minWidth: 0 }}>
                                                    <Typography
                                                        variant="subtitle1"
                                                        sx={{
                                                            fontWeight: 400,
                                                            whiteSpace: "nowrap",
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                            fontSize: { xs: "1rem", sm: "1rem" },
                                                            '&:hover': {
                                                                color: "#1E90FF",
                                                            },
                                                        }}
                                                    >
                                                        {course.description.length > 20
                                                            ? `${course.description.substring(0, 20)}...`
                                                            : course.description}
                                                    </Typography>
                                                </Box>

                                                <Typography variant="body2" sx={{ fontSize: { xs: "0.85rem", sm: "0.9rem" } }}>
                                                    📅 {formatDate(course.selectedDate)}
                                                </Typography>

                                                {/* Time */}
                                                <Box sx={{ flex: 2, minWidth: 100 }}>
                                                    <Typography variant="body2" sx={{ fontSize: { xs: "0.85rem", sm: "0.9rem" } }}>
                                                        ⏰ {formattedTime}
                                                    </Typography>
                                                </Box>

                                                {/* View Button */}
                                                <Box sx={{ flexShrink: 0, mt: { xs: 1, sm: 0 } }}>
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        sx={{
                                                            fontSize: "0.85rem",
                                                            px: 2,
                                                            py: 1,
                                                            textTransform: "none",
                                                            borderRadius: "6px",
                                                            backgroundImage: "linear-gradient(45deg, #2196F3, #1976D2)",
                                                            '&:hover': {
                                                                backgroundImage: "linear-gradient(45deg, #1976D2, #2196F3)",
                                                                transform: "scale(1.03)",
                                                            },
                                                            width: { xs: "100%", sm: "auto" },
                                                        }}
                                                    >
                                                        View
                                                    </Button>
                                                </Box>
                                            </Box>
                                        </Link>
                                    </Grid>

                                );
                            })}
                        </Grid>
                    </Box>
                </Box>
            </Box>
        </div>
    );
}

export default MyRequests;
